<template>
  <div class="settingHeader">
    <div class="settingHeader_breadcrumbs">
      <h1 v-if="breadcrumbs.length === 1" class="settingHeader_single-text">
        {{ breadcrumbs[0].text }}
      </h1>
      <v-breadcrumbs v-else :items="breadcrumbs" :class="customClasses">
        <template #divider>
          <v-icon size="22"> mdi-chevron-right </v-icon>
        </template>
        <template #item="{ item }">
          <v-breadcrumbs-item
            :to="item.to"
            :disabled="item.disabled"
            :link="item.link"
            class="settingHeader_breadcrumbs-item"
            :style="item.customStyles"
            :class="{ ['settingHeader_breadcrumbs--active']: item.active }"
          >
            <tooltip :classes="'settingHeader_breadcrumbs-item-tooltip'">
              <template #activator="{ on, attrs }">
                <div
                  v-bind="attrs"
                  class="settingHeader_breadcrumbs-itemText text-truncate"
                  v-on="on"
                >
                  {{ item.text }}
                </div>
              </template>
              <h2>{{ item.text }}</h2>
            </tooltip>
          </v-breadcrumbs-item>
        </template>
      </v-breadcrumbs>
      <span v-if="titleDesc" class="settingHeader_title_desc">{{
        titleDesc
      }}</span>
      <span class="settingHeader_subtitle">{{ desc }}</span>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
export default {
  name: 'SettingHeader',
  data() {
    return {
      breadcrumbs: [],
      desc: '',
      customClasses: '',
      titleDesc: null,
    };
  },
  computed: {
    ...mapState({
      cameraList: (state) => state.common.cameraList,
      registeredCams: (state) => state.settings.registeredCams,
      groupCams: (state) => state.settings.groupCams,
    }),
    activeSetting() {
      return this.$route.params.parent;
    },
  },
  watch: {
    $route(to) {
      this.setBreadcrumbs(to.name, to.params, to.query);
    },
  },
  mounted() {
    this.setBreadcrumbs(
      this.$route.name,
      this.$route.params,
      this.$route.query
    );
  },
  methods: {
    setBreadcrumbs(name, params, query) {
      const { child } = params;
      const breadcrumbs = [];
      this.customClasses = '';
      const customStyles = {
        width: 'calc(100vw - 1000px)',
        minWidth: '30px',
      };
      this.titleDesc = null;
      // settings camera list page
      if (name.startsWith('settings-firmware')) {
        breadcrumbs.push({
          text: 'カメラ一覧',
          disabled: false,
          to: { name: 'settings-firmware' },
          link: true,
        });
        this.desc = 'カメラ情報の確認、カメラ名の変更、ファームウェア更新';
        if (name === 'settings-firmware-details') {
          const camera = this.cameraList.find(
            (cam) => String(cam.id) === String(params.id)
          );
          breadcrumbs.push({
            text: camera ? camera.name : '',
            disabled: false,
            customStyles,
          });
          this.desc = '';
          this.customClasses = 'settingHeader_fullWidth maxWidth';
        } else if (name === 'settings-firmware-details-edit') {
          const camera = this.cameraList.find(
            (cam) => String(cam.id) === String(params.id)
          );
          breadcrumbs.push({
            text: camera ? camera.name : '',
            disabled: true,
            customStyles: {
              ...customStyles,
              maxWidth: 'fit-content',
            },
            to: {
              name: 'settings-firmware-details',
              params,
              query,
            },
            link: true,
          });
          this.desc = '';
          this.customClasses = 'settingHeader_fullWidth maxWidth';
          breadcrumbs.push({ text: 'カメラ名の編集', disabled: false });
        }
        if (name === 'settings-firmware-mode') {
          const camera = this.cameraList.find(
            (cam) => String(cam.id) === String(params.id)
          );
          breadcrumbs.push({
            text: camera ? camera.name : '',
            disabled: true,
            customStyles: {
              ...customStyles,
              maxWidth: 'fit-content',
            },
            to: {
              name: 'settings-firmware-details',
              params,
              query,
            },
            link: true,
          });
          breadcrumbs.push({ text: '夜間モード', disabled: false });
          this.desc = '';
          this.customClasses = 'settingHeader_fullWidth maxWidth';
        } else if (name === 'settings-firmware-motion') {
          const camera = this.cameraList.find(
            (cam) => String(cam.id) === String(params.id)
          );
          breadcrumbs.push({
            text: camera ? camera.name : '',
            disabled: true,
            customStyles: { maxWidth: '300px' },
            to: {
              name: 'settings-firmware-details',
              params,
              query,
            },
            link: true,
          });
          this.desc = '';
          this.customClasses = 'settingHeader_fullWidth';
          breadcrumbs.push({ text: '動体検知設定', disabled: false });
        }
      }
      // registered cams information
      if (name === 'settings-info') {
        breadcrumbs.push({
          text: 'カメラ情報登録',
          disabled: false,
          to: { name },
          link: true,
        });

        this.desc = 'カメラ情報の確認・削除、カメラ名の変更';
        if (child !== undefined) {
          this.registeredCams.find((cam) => {
            if (cam.id === child) {
              breadcrumbs.push({ text: cam.camName, disabled: false });
            }
          });
        }
      }
      // camera group settings
      if (name.startsWith('settings-cams')) {
        breadcrumbs.push({
          text: 'カメラグループ一覧',
          disabled: false,
          to: { name: 'settings-cams' },
          link: true,
        });
        this.desc = '登録したカメラを、グループに分けて設定';
        if (name === 'settings-cams-order') {
          breadcrumbs.push({ text: 'リスト順の編集', disabled: false });
        } else if (name === 'settings-cams-new') {
          breadcrumbs.push({
            text: 'カメラグループの新規作成',
            disabled: false,
          });
        } else if (name === 'settings-cams-list-detail') {
          const { id } = params;
          this.customClasses = 'settingHeader_fullWidth maxWidth';
          if (id !== undefined) {
            const cameraDetail = this.groupCams.find(
              (item) => String(item.id) === String(id)
            );
            breadcrumbs.push({
              text: cameraDetail.title,
              disabled: false,
              customStyles,
            });
          }
        } else if (name.startsWith('settings-cams-list-detail-edit')) {
          this.desc = '';
          let breadcrumbsText = 'グループの編集';
          const { id } = params;
          if (id !== undefined) {
            this.customClasses = 'settingHeader_fullWidth maxWidth';
            const cameraDetail = this.groupCams.find(
              (item) => String(item.id) === String(id)
            );
            breadcrumbs.push({
              text: cameraDetail.title,
              disabled: true,
              customStyles: {
                ...customStyles,
                maxWidth: 'fit-content',
              },
              to: {
                name: 'settings-cams-list-detail',
                params: { group: params.group, id: params.id },
              },
              link: true,
            });
          }
          if (name === 'settings-cams-list-detail-edit-sorting') {
            breadcrumbsText = 'リスト順の編集';
          }
          breadcrumbs.push({ text: breadcrumbsText, disabled: false });
        } else if (name === 'settings-cams-detail-edit') {
          const { id } = params;

          if (id !== undefined) {
            const cameraDetail = this.groupCams.find(
              (item) => String(item.id) === String(id)
            );
            this.customClasses = 'settingHeader_fullWidth';
            breadcrumbs.push(
              {
                text: cameraDetail.title,
                disabled: true,
                customStyles: { maxWidth: '300px' },
                to: {
                  name: 'settings-cams-list-detail',
                  params: { group: params.group, id: params.id },
                },
                link: true,
              },
              { text: 'グループの編集', disabled: false }
            );
            this.desc = '';
          }
        }
      }
      // download recorded video
      if (name === 'settings-record') {
        breadcrumbs.push({
          text: '録画映像のダウンロード',
          disabled: false,
          to: { name, query },
          link: true,
        });
        this.desc =
          'クラウドに保存している録画映像から、期間を指定してダウンロードします。';
        if (child !== undefined) {
          breadcrumbs.push({
            text: 'ダウンロードするカメラ',
            disabled: false,
            customStyles,
          });
          this.desc = 'ダウンロード対象のカメラを選択してください';
          this.customClasses = 'settingHeader_fullWidth';
        }
      }
      // history video list
      if (name.startsWith('settings-history')) {
        breadcrumbs.push({
          text: '履歴一覧',
          disabled: false,
          to: { name },
          link: true,
        });
        this.desc = 'アプリ操作履歴、イベント履歴、システム履歴の一覧';
      }

      if (name.startsWith('settings-users')) {
        breadcrumbs.push({
          text: 'ユーザー一覧',
          disabled: false,
          to: { name: 'settings-users' },
          link: true,
        });
        this.desc = 'ユーザーの新規登録、確認、削除、登録情報の変更';
        if (name === 'settings-users-new') {
          breadcrumbs.push({
            text: 'ユーザーの新規登録',
          });
          this.desc = '名前、メールアドレスの新規登録';
        } else if (name === 'settings-users-edit') {
          breadcrumbs.push({
            text: 'ユーザー情報の編集',
          });
          this.desc = 'ユーザー情報の編集、削除';
        } else if (name === 'settings-users-select-cam') {
          breadcrumbs.push({
            text: 'ユーザーの新規登録',
            disabled: true,
            to: { name: 'settings-users-new', query: this.$route.query },
            link: true,
          });
          breadcrumbs.push({
            text: '閲覧できるカメラグループ',
          });
          this.desc = '閲覧できるカメラグループを選択してください';
        } else if (name === 'settings-users-edit-select-cam') {
          breadcrumbs.push({
            text: 'ユーザー情報の編集',
            disabled: true,
            to: { name: 'settings-users-edit', params: this.$route.params },
            link: true,
          });
          breadcrumbs.push({
            text: '閲覧できるカメラグループ',
          });
          this.desc = '閲覧できるカメラグループを選択してください';
        }
      }
      if (name.startsWith('settings-icon')) {
        breadcrumbs.push({
          text: '組織アイコン変更',
          disabled: false,
          to: { name: 'settings-icon' },
          link: true,
        });
        this.desc = '組織を表すアイコンの変更';
      }
      if (name.startsWith('settings-notice')) {
        breadcrumbs.push({
          text: '通知機能の設定',
          disabled: false,
          to: { name: 'settings-notice' },
          link: true,
        });
        this.desc = '通知方法、対象のカメラグループの設定';
        if (name === 'settings-notice-push') {
          breadcrumbs.push({
            text: 'プッシュ通知',
          });
          this.desc = 'プッシュ通知の設定と対象項目';
        } else if (name === 'settings-notice-mail') {
          breadcrumbs.push({
            text: 'メール通知',
          });
          this.desc = 'メール通知の設定と対象項目';
        } else if (name === 'settings-notice-target') {
          breadcrumbs.push({
            text: '通知対象',
          });
          this.titleDesc = '通知を対象とするカメラグループの選択';
          this.desc = 'プッシュ通知・メール通知の両方で有効となります';
        }
      }
      if (name.startsWith('account-mfa')) {
        breadcrumbs.push({
          text: 'セキュリティ設定',
          disabled: false,
          to: { name: 'account-mfa' },
          link: true,
        });
        this.desc = '多要素認証によるセキュリティ設定';
        if (name === 'account-mfa-sms') {
          breadcrumbs.push({
            text: '電話番号に認証コードを送信する',
          });
          this.desc = '認証コードを送信する電話番号を設定';
        } else if (name === 'account-mfa-totp') {
          breadcrumbs.push({
            text: 'Authenticatorアプリを使用する',
          });
          this.desc = '認証コードを送信するAuthenticatorアプリを設定';
        } else if (name === 'account-mfa-nomfa') {
          breadcrumbs.push({
            text: '多要素認証を使用しない',
          });
          this.desc = 'ログイン時の多要素認証を解除';
        }
      }
      if (name.startsWith('settings-account')) {
        breadcrumbs.push({
          text: '組織の切り替え',
        });
        this.desc = 'ログインしている組織の切り替え';
      }
      this.breadcrumbs = breadcrumbs;
    },
  },
};
</script>

<style lang="scss">
.settingHeader {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  color: var(--text-color);
  width: 100%;
  &_breadcrumbs {
    max-width: 966px;
    &-item-tooltip {
      word-break: break-word;
      max-width: 350px;
      height: auto !important;
      width: auto !important;
      padding-bottom: 5px !important;
    }
  }
  &_single-text {
    font-size: 20px;
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
    letter-spacing: normal;
    text-align: left;
    color: #4b4b4b;
    margin: 5px 0;
  }
  &_text {
    font-size: 20px;
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.5;
    letter-spacing: normal;
    text-align: left;
    color: #4b4b4b;
  }
  &_type {
    color: var(--warning-color-lighten1);
    font-size: 12px;
    font-weight: 600;
  }
  .v-breadcrumbs__divider {
    padding: 0 5px !important;
    .theme--light.v-icon {
      color: #989898;
    }
  }
  .v-breadcrumbs {
    padding: 0 0 10px 0;
  }
  .theme--light.v-breadcrumbs .v-breadcrumbs__divider,
  .theme--light.v-breadcrumbs .v-breadcrumbs__item--disabled {
    color: #4b4b4b;
    font-weight: normal;
    pointer-events: all;
    cursor: pointer;
  }
  .v-breadcrumbs__item {
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.5;
    letter-spacing: normal;
    text-align: left;
    color: #4b4b4b;
    font-size: 20px;
    word-break: normal !important;
    overflow-wrap: anywhere !important;
  }
  &_title_desc {
    display: block;
    font-size: 14px;
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
    letter-spacing: normal;
    text-align: left;
    color: #4b4b4b;
    margin-bottom: 4px;
  }
  &_subtitle {
    font-size: 12px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    letter-spacing: normal;
    text-align: left;
    color: #989898;
  }
  &_fullWidth {
    .v-breadcrumbs__item {
      width: 100%;
    }
    &.maxWidth {
      width: max-content;
    }
  }
}
</style>
